// src/components/admin/SubscriptionManager.js
import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../../services/subscriptionService';
import { companyService } from '../../services/apiServices';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import StatusBadge from './StatusBadge';

const SubscriptionManager = ({ companyId, onClose, onUpdate }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [subscription, setSubscription] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    duration: 30,
    durationType: 'days',
    startDate: new Date().toISOString().split('T')[0],
    paymentStatus: 'pending',
    notes: ''
  });

  // Load data on mount
  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load company and subscription data in parallel
      const [companyResponse, subscriptionResponse] = await Promise.allSettled([
        companyService.getCompanyById(companyId),
        subscriptionService.getSubscriptionByCompany(companyId)
      ]);

      // Handle company data
      if (companyResponse.status === 'fulfilled' && companyResponse.value.success) {
        setCompany(companyResponse.value.data);
      } else {
        throw new Error('Impossible de charger les données de l\'entreprise');
      }

      // Handle subscription data
      if (subscriptionResponse.status === 'fulfilled' && subscriptionResponse.value.success) {
        setSubscription(subscriptionResponse.value.data);
      } else {
        // Default subscription structure if not found
        setSubscription({
          companyId,
          status: 'inactive',
          startDate: null,
          endDate: null,
          isActive: false,
          daysRemaining: 0,
          paymentStatus: 'pending',
          notes: ''
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateEndDate = () => {
    const startDate = new Date(formData.startDate);
    const endDate = new Date(startDate);
    
    switch (formData.durationType) {
      case 'days':
        endDate.setDate(endDate.getDate() + parseInt(formData.duration));
        break;
      case 'months':
        endDate.setMonth(endDate.getMonth() + parseInt(formData.duration));
        break;
      case 'years':
        endDate.setFullYear(endDate.getFullYear() + parseInt(formData.duration));
        break;
    }
    
    return endDate;
  };

  const handleActivate = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const endDate = calculateEndDate();
      const activationData = {
        startDate: new Date(formData.startDate).toISOString(),
        endDate: endDate.toISOString(),
        duration: formData.duration,
        durationType: formData.durationType,
        notes: formData.notes,
        paymentStatus: formData.paymentStatus
      };

      const response = await subscriptionService.activateSubscription(companyId, activationData);
      
      if (response.success) {
        showSuccessToast('Abonnement activé avec succès!');
        await loadData(); // Refresh data
        
        // Trigger subscription refresh event for SubscriptionGuard
        window.dispatchEvent(new CustomEvent('subscription:refresh'));
        
        if (onUpdate) onUpdate();
        onClose();
      } else {
        throw new Error(response.message || 'Échec de l\'activation');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de l'activation: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const response = await subscriptionService.deactivateSubscription(
        companyId, 
        'Désactivation manuelle par superadmin'
      );
      
      if (response.success) {
        showSuccessToast('Abonnement désactivé avec succès!');
        await loadData(); // Refresh data
        
        // Trigger subscription refresh event for SubscriptionGuard
        window.dispatchEvent(new CustomEvent('subscription:refresh'));
        
        if (onUpdate) onUpdate();
        onClose();
      } else {
        throw new Error(response.message || 'Échec de la désactivation');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de la désactivation: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtend = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const extensionData = {
        duration: formData.duration,
        durationType: formData.durationType,
        notes: formData.notes,
        paymentStatus: formData.paymentStatus
      };

      const response = await subscriptionService.extendSubscription(companyId, extensionData);
      
      if (response.success) {
        showSuccessToast('Abonnement étendu avec succès!');
        await loadData(); // Refresh data
        
        // Trigger subscription refresh event for SubscriptionGuard
        window.dispatchEvent(new CustomEvent('subscription:refresh'));
        
        if (onUpdate) onUpdate();
        onClose();
      } else {
        throw new Error(response.message || 'Échec de l\'extension');
      }
    } catch (err) {
      const errorMessage = `Erreur lors de l'extension: ${err.message}`;
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'danger';
      case 'inactive': return 'warning';
      default: return 'secondary';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isActive = subscription?.status === 'active';

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            Gestion d'abonnement - {company?.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={submitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Current status */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-4">Statut actuel</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <StatusBadge 
                  status={isActive ? 'Actif' : subscription?.status === 'expired' ? 'Expiré' : 'Inactif'} 
                  type={getStatusColor(subscription?.status)} 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jours restants</label>
                <p className="text-lg font-semibold text-gray-900">
                  {subscription?.daysRemaining || 0}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <p className="text-gray-900">{formatDate(subscription?.startDate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date d'expiration</label>
                <p className="text-gray-900">{formatDate(subscription?.endDate)}</p>
              </div>
            </div>
          </div>

          {/* Action form */}
          <div className="bg-white border rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-4">
              {isActive ? 'Étendre l\'abonnement' : 'Activer l\'abonnement'}
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Durée</label>
                <input
                  type="number"
                  min="1"
                  value={formData.duration}
                  onChange={(e) => handleFormChange('duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={formData.durationType}
                  onChange={(e) => handleFormChange('durationType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={submitting}
                >
                  <option value="days">Jours</option>
                  <option value="months">Mois</option>
                  <option value="years">Années</option>
                </select>
              </div>
            </div>

            {!isActive && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleFormChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={submitting}
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut de paiement</label>
              <select
                value={formData.paymentStatus}
                onChange={(e) => handleFormChange('paymentStatus', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={submitting}
              >
                <option value="pending">En attente</option>
                <option value="paid">Payé</option>
                <option value="failed">Échec</option>
                <option value="free">Gratuit</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Notes optionnelles..."
                disabled={submitting}
              />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              disabled={submitting}
            >
              Annuler
            </button>
            
            {isActive && (
              <>
                <button
                  onClick={handleExtend}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Extension...' : 'Étendre'}
                </button>
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Désactivation...' : 'Désactiver'}
                </button>
              </>
            )}
            
            {!isActive && (
              <button
                onClick={handleActivate}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Activation...' : 'Activer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionManager;